import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { axios } from "@/admin/libs/axios";

import type { PlanType } from "./campaigns";

export interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  username: string;
  provider: "email" | "github" | "google";
  createdAt: string;
  emailVerified: boolean;
  plan: PlanType;
  stripeCustomerId: string | null;
  subscriptionStatus: string | null;
  lastSignedIn: string | null;
  role: "user" | "admin";
}

export interface AdminUserDetail extends AdminUserRow {
  picture: string | null;
  locale: string;
  twoFactorEnabled: boolean;
  subscription: {
    plan: PlanType;
    status: string;
    stripeSubscriptionId: string | null;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  campaigns: Array<{
    id: string;
    campaignSlug: string;
    campaignName: string;
    kind: "grant" | "conversion";
    grantedPlan: PlanType;
    grantedAt: string;
    expiresAt: string | null;
    isActive: boolean;
    discountPercent: number | null;
  }>;
  deepLinks: {
    stripeCustomer: string | null;
    posthogPerson: string | null;
  };
  counts: {
    resumes: number;
    coverLetters: number;
    applications: number;
    interviews: number;
  };
}

export interface DeleteAdminUserPayload {
  confirmationEmail: string;
}

export interface DeleteAdminUserResult {
  deleted: true;
  id: string;
  counts: Record<string, number>;
  stripeCustomerDeleted: boolean;
  volatileKeysRequested: number;
}

export const searchAdminUsers = async (query: string): Promise<AdminUserRow[]> => {
  const { data } = await axios.get<AdminUserRow[]>("/admin/users", {
    params: { q: query },
  });
  return data;
};

export const fetchAdminUser = async (id: string): Promise<AdminUserDetail> => {
  const { data } = await axios.get<AdminUserDetail>(`/admin/users/${id}`);
  return data;
};

export const deleteAdminUser = async (
  id: string,
  payload: DeleteAdminUserPayload,
): Promise<DeleteAdminUserResult> => {
  const { data } = await axios.delete<DeleteAdminUserResult>(`/admin/users/${id}`, {
    data: payload,
    headers: { "x-admin-intent": "delete-user" },
  });
  return data;
};

export const useAdminUsers = (query: string) =>
  useQuery({
    queryKey: ["admin", "users", query],
    queryFn: () => searchAdminUsers(query),
    // Never show stale matches from a previous query once the term changes.
    placeholderData: (previous) => previous,
    staleTime: 10_000,
  });

export const useAdminUser = (id: string | undefined) =>
  useQuery({
    queryKey: ["admin", "users", "detail", id],
    queryFn: () => fetchAdminUser(id as string),
    enabled: Boolean(id),
  });

export const useDeleteAdminUser = (id: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DeleteAdminUserPayload) => {
      if (!id) throw new Error("Missing user id.");
      return deleteAdminUser(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      if (id) queryClient.removeQueries({ queryKey: ["admin", "users", "detail", id] });
    },
  });
};
